import React, { useMemo } from 'react';
import {
  BreakpointQueryName, SpacingName,
} from '@doveit/lego';
import { NextPage } from 'next';
import { buildNotFound } from '../client/utils/error/http/httpErrors';
import { requireResourcesFromStaticAssets } from '../client/utils/assetsUtils';
import BottomBar, { BottomBarProps } from '../client/templates/common/bottom-bar/BottomBar';
import ArticleMarkdown from '../client/components/article-markdown/ArticleMarkdown';
import scrollToRef from '../client/utils/scroll-to-ref/scrollToRef';
import { slugIt } from '../client/utils/links';
import * as styles from '../client/templates/evaluation-by-locality/EvaluationByLocality.style';
import AppLayout from '../client/layouts/app-layout/AppLayout';
import { EVALUATION_BY_LOCALITY_SLUG } from '../client/graphql/queries/evaluation';
import { template } from '../client/utils/string/string';
import PageSection from '../client/components/page-section/PageSection';
import RapidLinks from '../client/templates/common/rapid-links/RapidLinks';
import PageLayout from '../client/layouts/page-layout/PageLayout';
import { getStructuredData, getBreadcrumbStructuredData } from '../data/structured';
import PageHead, { PageHeadProps } from '../client/components/page-head/PageHead';
import { assertNotNullOrUndefined } from '../client/utils/error/error';
import buildBreadcrumb from '../client/utils/breadcrumb/evaluation';
import { HeroSection, TextAlign } from '../client/types';
import CreateEvaluation from '../client/containers/create-evaluation/CreateEvaluation';
import { sendEvaluationFormSubmitEventAndRedirect } from '../client/utils/tracking';
import useRefInView from '../client/hooks/useRefInView/useRefInView';
import Hero from '../client/templates/common/hero/Hero';
import { withGetServerSideError, withError } from '../client/hocs/with-server-side-error/withServerSideError';
import { getEvaluationStructuredData } from '../data/structured/evaluation';
import initGraphQLClient from '../client/graphql/initGraphQLClient';

export interface EvaluationByLocalityProps {
  SEO: PageHeadProps['SEO'],
  hero: HeroSection,
  bottomBar: BottomBarProps,
  evaluation: {
    locality: {
      slug: string,
      name: string,
    },
    articleBlocks: {
      title: string,
      content: string,
    }[],
  },
}

export const EvaluationByLocality: NextPage<EvaluationByLocalityProps> = ({
  SEO,
  hero,
  bottomBar,
  evaluation,
}) => {
  const { current, ref: formRef, isVisible: isFormVisible } = useRefInView({
    threshold: 0,
  });

  const anchors = useMemo(() => evaluation.articleBlocks.slice(1).map((block) => block.title), [evaluation]);

  const breadcrumb = buildBreadcrumb(evaluation.locality.name);

  return (
    <AppLayout>
      <PageHead
        SEO={SEO}
        structuredData={getStructuredData(
          getBreadcrumbStructuredData(breadcrumb),
          getEvaluationStructuredData(evaluation.locality.name),
        )}
      />

      <Hero
        title={hero.title}
        description={hero.description}
        secondarySlotTitle={hero.formTitle}
        backgroundImage={requireResourcesFromStaticAssets(hero.backgroundImage)}
        secondarySlot={(
          <div ref={formRef}>
            <CreateEvaluation
              onSubmit={() => sendEvaluationFormSubmitEventAndRedirect('hero:create-evaluation')}
            />
          </div>
        )}
      />

      {evaluation.articleBlocks.map((article, index) => (
        <div
          key={article.title}
          css={styles.articleBlock}
        >
          <div
            id={slugIt(article.title)}
            css={styles.anchor}
          />
          <PageSection>
            <PageSection.Wrapper>
              <PageSection.Title textAlign={TextAlign.CENTER}>
                {article.title}
              </PageSection.Title>
              <PageSection.Block>
                <ArticleMarkdown>
                  {article.content}
                </ArticleMarkdown>
                {index === 0 && anchors.length > 0 && (
                  <ul css={styles.anchorList}>
                    {anchors.map((anchor) => (
                      <li
                        css={styles.anchorListItem}
                        key={anchor}
                      >
                        <a href={`#${slugIt(anchor)}`}>
                          {anchor}
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </PageSection.Block>
            </PageSection.Wrapper>
          </PageSection>
        </div>
      ))}

      <PageSection padding={[0, 0, SpacingName.MEDIUM]}>
        <PageSection.Wrapper>
          <PageSection.Block>
            <RapidLinks
              locality={evaluation.locality}
              withoutPad
              showAgency
            />
          </PageSection.Block>
        </PageSection.Wrapper>
      </PageSection>
      <PageLayout.Wrapper>
        <PageLayout.Breadcrumb items={breadcrumb} />
      </PageLayout.Wrapper>

      <BottomBar
        hiddenFrom={BreakpointQueryName.MD}
        expanded={!isFormVisible}
        label={bottomBar.label}
        title={bottomBar.title}
        headline={bottomBar.headline}
        onClick={scrollToRef(current)}
      />
    </AppLayout>
  );
};

export const getServerSideProps = withGetServerSideError<EvaluationByLocalityProps>(async ({ query }) => {
  const [data] = await Promise.all([
    import('../data/pages/evaluation-by-locality.yml'),
  ]);

  const { locality } = query;
  const slug = locality && (locality as string).toLowerCase();

  const graphQLParams = {
    query: EVALUATION_BY_LOCALITY_SLUG,
    variables: {
      slug,
    },
  };
  const graphQLClient = initGraphQLClient();
  const { data: { evaluations: [evaluation] } } = await graphQLClient.query(graphQLParams);
  assertNotNullOrUndefined(evaluation, buildNotFound(`Evaluation with slug "${slug}" not found`));

  const SEO = template(data.SEO, { locality: evaluation.locality.name });

  const hero = template(data.hero, { locality: evaluation.locality.name });

  return {
    props: {
      ...data,
      SEO,
      hero,
      evaluation,
    },
  };
});

export default withError(EvaluationByLocality);
